/**
 * Who a memory saved from this thread may be attributed to (web mirror).
 *
 * Kept identical to `memoryTargetsForThread` in the mobile app and to
 * `_permitted_memory_targets` in `backend/app/main.py`. The backend is the
 * authority and rejects anything outside the set; this only decides whether
 * the UI must ask, and which rows to offer.
 *
 * Rules:
 *   self / general single-subject thread -> {self}
 *   non-self single-subject thread       -> {self, active_subject_id}
 *   compatibility thread                 -> {self, ...compat_subject_ids}
 *
 * `self` is always permitted: a user can say something about themselves in
 * anyone's thread. `addressed_subject_id` may choose the DEFAULT but can never
 * add a member — it is derived from conversation content, so allowing it to
 * expand the set would let a thread's own text widen its permissions.
 */

export type ThreadSubjectContract = {
  active_subject_id?: string | null;
  active_subject_mode?: 'single' | 'compat_pair' | string | null;
  compat_subject_ids?: string[] | null;
  addressed_subject_id?: string | null;
};

export type MemoryTargets = {
  targets: string[];
  defaultTarget: string | null;
  needsChoice: boolean;
  invalid: boolean;
};

export function memoryTargetsForThread(
  thread: ThreadSubjectContract | null | undefined,
): MemoryTargets {
  const invalid: MemoryTargets = {
    targets: [],
    defaultTarget: null,
    needsChoice: false,
    invalid: true,
  };
  if (!thread) {
    return { targets: ['self'], defaultTarget: 'self', needsChoice: false, invalid: false };
  }

  const mode = thread.active_subject_mode || 'single';
  const active = (thread.active_subject_id || 'self').trim() || 'self';
  const compat = (thread.compat_subject_ids || [])
    .map((v) => String(v).trim())
    .filter(Boolean);

  let targets: string[];
  if (mode === 'compat_pair') {
    if (compat.length === 0) return invalid;
    targets = ['self', ...compat];
  } else if (mode === 'single') {
    targets = active === 'self' ? ['self'] : ['self', active];
  } else {
    return invalid;
  }

  const seen = new Set<string>();
  const ordered = targets.filter((t) => (seen.has(t) ? false : (seen.add(t), true)));

  const addressed = (thread.addressed_subject_id || '').trim();
  const defaultTarget = ordered.includes(addressed)
    ? addressed
    : ordered.includes(active)
    ? active
    : null;

  return { targets: ordered, defaultTarget, needsChoice: ordered.length > 1, invalid: false };
}

/** Stable target-contract error code from a failed save, if present. */
export function memoryTargetErrorCode(err: any): string | null {
  const detail = err?.response?.data?.detail ?? err?.detail;
  if (typeof detail !== 'string') return null;
  return detail.startsWith('memory_') ? detail : null;
}
